import { useRef } from "react";
import { Typography, Box, Button } from "@mui/material";
import { getLayoutLine } from "../../../components/layout/getLayoutLine";
import { postDownload } from "../../../fetch/postDownload";
export function Platform(props) {
  const { panel: __panel__ } = props;
  const __ref__ = useRef(null);
  return (
    <Box className="pk__page">
      <Box className="pk__block">
        <Box className="pk__info">
          <Typography variant="caption" color="secondary.dark">
            Last updated July, 24 2026
          </Typography>
          <Button
            className="link"
            component="a"
            onClick={() =>
              postDownload("polycaik_platform_agreement.pdf", __ref__.current)
            }
          >
            Download (PDF)
          </Button>
        </Box>
        <Typography variant="h4" color="#606060" paddingTop={5}>
          <strong>Polycaik Platform Agreement </strong>
        </Typography>
        <Box className="pk__body" ref={__ref__}>
          {getLayoutLine([
            <>
              This Polycaik Platform Agreement (the “
              <span className="token">Platform Agreement</span>”) is between
              Polycaik, with offices at 155 Willowbrook Boulevard, Suite 110
              #6765, Wayne, New Jersey 07470 (“
              <span className="token">Polycaik</span>”) and the person or entity
              agreeing to this Platform Agreement in the manner specified below
              (“<span className="token">Participant</span>
              ,” “<span className="token">you</span>,” or “
              <span className="token">your</span>”) (each a “
              <span className="token">Party</span>” and together the “
              <span className="token">Parties</span>”).
            </>,
            <>
              <span className="token header caution">WHEREAS,</span> Polycaik
              has developed and operates an online platform that permits
              Contributors to submit Game Content, Developers to incorporate
              Game Content into Games, and Gamers to play such Games (the “
              <span className="token">Polycaik Platform</span>,” as further
              defined below);
            </>,
            <>
              <span className="token header caution">WHEREAS,</span> you either
              wish to post Game Content to the Polycaik Platform; to use Game
              Content in creation of Games for the Polycaik Platform; or to play
              Games posted on the Polycaik Platform; and
            </>,
            <>
              <span className="token header caution">WHEREAS,</span> Polycaik is
              willing to provide access to the Polycaik Platform for such
              purposes, all in accordance with the terms and conditions set out
              below;
            </>,
            <>
              <span className="token header caution">NOW, THEREFORE,</span> in
              consideration of the mutual promises set forth in this Platform
              Agreement, the Parties, intending to be legally bound, agree as
              follows:
            </>,
          ])}
          <Box className="stub stub--title">
            <span className="token token--slot">1.</span>
            <div>
              <span className="token header">Acceptance of Terms.</span>By
              clicking “I accept” in connection with the onboarding process for
              the Platform, you accept and agree to be bound by the terms and
              conditions set out in this Platform Agreement. These terms and
              conditions contractually bind you and Polycaik. If you do not
              agree to these terms and conditions, you will not be permitted to,
              and must not, use the Platform or the Services.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token token--slot">2.</span>
            <div>
              <span className="token header">Definitions.</span>Capitalized
              terms have the meanings set forth in{" "}
              <button
                className="link"
                onClick={() => __panel__("__glossary__")}
              >
                Uniform Definitions
              </button>
              .
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token token--slot">3.</span>
            <div>
              <span className="token header">Polycaik Platform.</span>The
              Polycaik Platform brings together Contributors, Developers, and
              Gamers for purposes of developing, commercializing, and playing
              Games.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">3.1.</span>
            <div>
              <span className="token header">Platform Hosting.</span>Polycaik
              operates and maintains the Platform in a cloud-based environment
              hosted on third-party infrastructure, and your interactions with
              the Platform occur through this cloud-hosted environment.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">3.2.</span>
            <div>
              <span className="token header">
                Platform Features; Access Plans.
              </span>
              Access to and use of certain features, Game Content, Games, tools,
              and data within the Platform are organized into different access
              and pricing levels corresponding to your role as a Gamer,
              Contributor, or Developer (the “
              <span className="token">Access Plan</span>”) set out in{" "}
              <button className="link" onClick={() => __panel__("__access__")}>
                Access Plans
              </button>
              .
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">3.3.</span>
            <div>
              <span className="token header">Security.</span>Polycaik shall use
              commercially reasonable efforts to safeguard the security of
              Participant Content, and shall employ for this purpose information
              security controls consistent with accepted practice in the
              industry and applicable law. Your Personal Information is governed
              by Section 14 (Your Consent to Polycaik Privacy Practices).
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">3.4.</span>
            <div>
              <span className="token header">
                Support Services & Maintenance.
              </span>
              You may submit support requests via email to contact@polycaik.com
              or through the support or messaging functionality that Polycaik
              may make available on the Platform. Polycaik will use commercially
              reasonable efforts to respond to such support requests in a timely
              manner, commensurate with the severity of the error.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token token--slot">4.</span>
            <span className="token header">
              Accessing and Using the Polycaik Platform.
            </span>
          </Box>
          <Box className="stub">
            <span className="token token--slot">4.1.</span>
            <div>
              <span className="token header">Onboarding.</span>To participate on
              the Platform and receive Services, you must first be approved by
              Polycaik. Upon approval, you will create an account in accordance
              with the instructions provided on the Platform. Polycaik may
              accept or reject applicants in its discretion. You are responsible
              for keeping your account secure and ensuring against unauthorized
              use or disclosure of your account login credentials. You will be
              responsible (and liable) for activity taking place on your account
              and use of the Polycaik Platform under your account.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">4.2.</span>
            <div>
              <span className="token header">Contributing Game Content.</span>If
              your Access Plan permits you to act as a Contributor, you will be
              entitled to post Game Content to the Polycaik Platform for use by
              Developers in creating Games. You hereby agree that, by posting
              Game Content to the Platform, you become bound by the Game
              Development Agreement attached to this Platform Agreement as{" "}
              <button
                className="link"
                onClick={() => __panel__("__developer__")}
              >
                Schedule A
              </button>
              . The Game Development Agreement governs the relationship between
              you (acting as a Contributor) and Developers with respect to their
              use of your Game Content. You must comply with the Game
              Development Agreement as to Game Content you submit to the
              Platform, and Developers are entitled to enforce the Game
              Development Agreement against you if you fail to meet your
              obligations under such Game Development Agreement.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">4.3.</span>
            <div>
              <span className="token header">Developing Games.</span>If your
              Access Plan permits you to act as a Developer, you will be
              entitled to create Games, using Contributors’ Game Content, for
              use on the Polycaik Platform by Gamers. You hereby agree that by
              using a Contributor’s Game Content you become bound by the Game
              Development Agreement attached to this Platform Agreement as{" "}
              <button
                className="link"
                onClick={() => __panel__("__developer__")}
              >
                Schedule A
              </button>
              . The Game Development Agreement governs the relationship between
              you (acting as a Developer) and Contributors with respect to your
              use of their Game Content. You must comply with the Game
              Development Agreement as to Game Content you use in your Games,
              and Contributors are entitled to enforce the Game Development
              Agreement against you if you fail to meet your obligations under
              such Game Development Agreement.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">4.4.</span>
            <div>
              <span className="token header">Posting Games.</span>If your Access
              Plan permits you to act as a Developer, you will also be entitled
              to post your Games to the Platform for use by Gamers. You hereby
              agree that, by posting a Game to the Platform, you become bound by
              the Gamer Agreement attached as{" "}
              <button
                className="link"
                onClick={() => __panel__("__consumer__")}
              >
                Schedule B
              </button>
              . The Gamer Agreement governs the relationship between you (acting
              as a Developer) and Gamers with respect to their use of your
              Games. You must comply with the Gamer Agreement as to your Games,
              and Gamers are entitled to enforce the Gamer Agreement against you
              if you fail to meet your obligations under such Gamer Agreement.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">4.5.</span>
            <div>
              <span className="token header">Playing Games.</span>If your Access
              Plan permits you to act as a Gamer, you will be entitled to play
              Games on the Polycaik Platform, and to create and post
              User-Generated Content with respect to Games. You hereby agree
              that, by posting a Game to the Platform, you become bound by the
              Gamer Agreement attached as{" "}
              <button
                className="link"
                onClick={() => __panel__("__consumer__")}
              >
                Schedule B
              </button>
              . The Gamer Agreement governs the relationship between you (acting
              as a Gamer) and Developers with respect to your use of their
              Games. You must comply with the Gamer Agreement, and Developers
              are entitled to enforce the Gamer Agreement against you if you
              fail to meet your obligations under such Gamer Agreement.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">4.6.</span>
            <div>
              <span className="token header">
                Polycaik’s Role with respect to the Game Development Agreement
                and Gamer Agreement.
              </span>
              You hereby acknowledge (i) that Polycaik is not a party to either
              the Game Development Agreement or the Gamer Agreement; (ii) that
              you are not entitled to enforce (or seek to enforce) such
              Agreements against Polycaik; and (iii) that your relationship, and
              rights and obligations as to Polycaik, are solely contained within
              this Platform Agreement.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">4.7.</span>
            <div>
              <span className="token header">Participant Systems.</span>You
              shall, at your sole expense, obtain (i) the IT infrastructure
              needed to access and use the Polycaik Platform, and (ii) all third
              party software that you wish to integrate into, or otherwise
              interface with, the Polycaik Platform (to the extent Polycaik
              provides applicable APIs) (collectively, “
              <span className="token">Participant Systems</span>”). You shall
              ensure that Participant Systems comply with the Requirements, and
              Polycaik shall not be responsible for errors attributable to a
              failure of Participant Systems to comply with such Requirements.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token token--slot">5.</span>
            <span className="token header">Participant Content.</span>
          </Box>
          <Box className="stub">
            <span className="token token--slot">5.1.</span>
            <div>
              <span className="token header">
                Content Specifications and Guidelines.
              </span>
              All Participant Content submitted to the Platform by you must
              strictly adhere to the Requirements.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">5.2.</span>
            <div>
              <span className="token header">Community Review.</span>
              At its option, Polycaik, together with community members selected
              by Polycaik, will engage in a review process to ensure that your
              Games and Game Content (as applicable) meet the Requirements
              (collectively, “<span className="token">Community Reviewers</span>
              ”). If approved by Community Reviewers, the Games or Game Content
              will then be published for use on the Platform.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">5.3.</span>
            <div>
              <span className="token header">Content Moderation.</span>
              Notwithstanding Section 5.2 (Community Review), Polycaik (and
              Community Reviewers) assume no obligation to screen or monitor
              Participant Content. However, and as further set out in Section 20
              (Monitoring Polycaik Platform Use), Polycaik reserves the right, but does
              not assume the obligation, to review, screen, reject, remove, or
              require modification of any Participant Content at any time and
              for any reason in its sole discretion, with or without notice.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">5.4.</span>
            <div>
              <span className="token header">Data Backup.</span>
              You are responsible for retaining copies of Participant Content
              and, unless otherwise provided in your Access Plan, Polycaik has
              no obligation to backup Participant Content. In addition, unless
              otherwise provided in your Access Plan,{" "}
              <span className="token header caution">
                POLYCAIK HAS NO OBLIGATION OR LIABILITY FOR ANY ALTERATION, OR
                DAMAGE TO PARTICIPANT CONTENT, OR FOR THE RECOVERY OF
                PARTICIPANT CONTENT
              </span>
              .
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">5.5.</span>
            <div>
              <span className="token header">Portability of Games.</span>
              To the extent the Platform’s functionality allows, you will be
              entitled to migrate to other platforms and otherwise re-use Games
              you have developed on the Platform; provided, however, that you
              are prohibited from using any Game Content you license under a
              Game Development Agreement outside the Platform.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">5.6.</span>
            <div>
              <span className="token header">Portability of Game Content.</span>
              To the extent the Platform’s functionality allows, you will be
              entitled to migrate to other platforms and otherwise re-use Game
              Content you have submitted to the Platform.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token token--slot">6.</span>
            <div>
              <span className="token header">Content Library.</span>
              At its option, Polycaik will make Game Content available via a
              centralized repository (the “
              <span className="token">Content Library</span>”), as follows.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">6.1.</span>
            <div>
              <span className="token header">Library Search.</span>
              Polycaik will, at its option, assign items of approved Game
              Content in the Content Library a unique identifier and index,
              catalog, or otherwise make discoverable such Game Content through
              search, filtering, and browsing tools within the Content Library.
              If Polycaik chooses to structure the Content Library in this
              manner, Polycaik will be entitled to determine, in its sole
              discretion, how items in the Content Library are categorized,
              tagged, ranked, and presented to Participants.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">6.2.</span>
            <div>
              <span className="token header">Game Elements.</span>
              You (acting as a Contributor) may group items of your Game Content
              into bundles for purchase by Developers under your Game
              Development Agreement (each bundle a “
              <span className="token">Game Element</span>”). Polycaik may
              approve, reject, or require modifications to Game Elements as a
              condition to its inclusion of such Game Elements in the Content
              Library.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">6.3.</span>
            <div>
              <span className="token header">Purchases from the Library.</span>
              You (acting as a Developer) may access the Content Library and,
              subject to your Access Plan, request, license, and/or purchase
              Game Elements through the Content Library.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token token--slot">7.</span>
            <span className="token header">
              Marketing of Participant Content and Platform.
            </span>
          </Box>
          <Box className="stub">
            <span className="token token--slot">7.1.</span>
            <div>
              <span className="token header">Standard Visibility.</span>
              Polycaik will provide reasonable visibility on the Platform (i)
              for your Game Content (where you are acting as a Contributor) and
              (ii) for your Games (where you are acting as a Developer) (“
              <span className="token">Standard Visibility</span>”). Other than
              such Standard Visibility, Polycaik has no obligation under this
              Platform Agreement to market, advertise, or otherwise promote any
              particular Participant, Game Content, or Game. Any Polycaik tools
              or features that may affect the visibility or ranking of Game
              Content or Games are provided by Polycaik on an “as is” and “as
              available” basis.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">7.2.</span>
            <div>
              <span className="token header">
                Featured Games and Game Content; Platform Promotion.
              </span>
              Polycaik may, in its sole discretion, provide a particular
              Participant (or such your Games or Game Content) with visibility
              beyond Standard Visibility, and showcase, feature, or otherwise
              use your Game Content, Games, name, trademarks, and logos (as
              applicable) for purposes of (i) promoting and advertising such
              Participant, Game Content, or Games and (ii) promoting and
              advertising the Platform and the Services. Such marketing or
              promotional use by Polycaik will not entitle you to additional
              compensation.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">7.3.</span>
            <div>
              <span className="token header">In-Game Advertising.</span>
              In-game advertisements are not permitted within your Game, Game
              Content, or User-Generated Content. Polycaik may, in its sole
              discretion and on a case-by-case basis, grant written consent for
              in-game advertising. If Polycaik grants such consent, the Parties
              may enter into a mutually agreed-upon written agreement governing
              the terms applicable to such in-game advertising arrangement.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token token--slot">8.</span>
            <span className="token header">Grant of Rights.</span>
          </Box>
          <Box className="stub">
            <span className="token token--slot">8.1.</span>
            <div>
              <span className="token header">Participant Access Rights.</span>
              Polycaik, under its Intellectual Property Rights and during the
              Term, hereby grants to you a limited, non-exclusive,
              non-transferrable, and non-sublicensable license to access and use
              the Polycaik Platform in accordance with the Documentation and
              your Access Plan. You acknowledge that the rights granted to you
              pursuant to this Section 8.1 (Participant Access Rights) are
              expressly limited to: (i) cloud-based access to an instance of the
              Polycaik Platform that resides in object code form on servers
              controlled by Polycaik; (ii) via a mechanism approved by Polycaik;
              and (iii) the then-current version of the Polycaik Platform
              generally made available by Polycaik to all Participants.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">8.2.</span>
            <div>
              <span className="token header">Use of Documentation.</span>
              Polycaik, under its Intellectual Property Rights and during the
              Term, hereby grants to you a limited, non-exclusive,
              non-transferrable, and non-sublicensable license to access and use
              the Documentation, solely in connection with your access to and
              use of the Polycaik Platform in accordance with Section 8.1
              (Participant Access Rights).
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">8.3.</span>
            <div>
              <span className="token header">
                License to Participant Content.
              </span>
              You hereby grant to Polycaik, under your Intellectual Property
              Rights, a non-exclusive, non-sublicenseable, non-transferable,
              license to display, publish, modify, reproduce, and otherwise use
              Participant Content, solely for purposes of Polycaik’s performance
              of its obligations to you under this Platform Agreement. The
              license set out in this Section 8.3 (License to Participant
              Content) shall remain in effect throughout the Term and, in the
              case of Game Content and User-Generated Content, continue beyond
              the Term in accordance with Section 24.6 (Game Continuity and
              Continuing Rights).
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">8.4.</span>
            <div>
              <span className="token header">
                No Reverse Engineering; No Circumvention.
              </span>
              You shall not use the Platform or the Services other than as
              authorized in this Platform Agreement. You shall not (and shall
              not assist a third party to): (i) decompile, disassemble, or
              reverse engineer the Platform; or (ii) attempt to bypass, delete
              or disable any security features of the Platform.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">8.5.</span>
            <div>
              <span className="token header">Feedback.</span>
              If you choose to provide Polycaik with suggestions,
              recommendations, and ideas relating to the Polycaik Platform or
              Polycaik’s business (collectively, the “
              <span className="token">Feedback</span>”), you hereby grant to
              Polycaik, under your Intellectual Property Rights and during and
              after the Term, a non-exclusive, perpetual, worldwide,
              irrevocable, fully paid-up, sublicensable, and transferrable
              license to use, modify, and exploit such Feedback for any purpose.
              No compensation shall be paid by Polycaik with respect to its use
              of Feedback.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">8.6.</span>
            <div>
              <span className="token header">No Implied Rights.</span>
              Nothing in this Platform Agreement shall be construed as granting
              you any rights to access the Polycaik Platform or Documentation
              other than those expressly provided in this Section 8 (Grant of
              Rights), and there shall be no implied rights pursuant to this
              Platform Agreement, based on any course of conduct or other
              construction or interpretation thereof. All rights and licenses
              not expressly granted to you in this Platform Agreement are
              expressly reserved by Polycaik.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token token--slot">9.</span>
            <span className="token header">Ownership.</span>
          </Box>
          <Box className="stub">
            <span className="token token--slot">9.1.</span>
            <div>
              <span className="token header"> Ownership of Platform.</span>
              Other than those rights expressly granted to you under this
              Platform Agreement, as between you and Polycaik, Polycaik retains
              all right, title, and interest in and to: (i) the Polycaik
              Platform; (ii) Polycaik Confidential Information; (iii) the
              Documentation; (iv) any data, software code, or other content
              (excluding Participant Content and Personal Information) input,
              output, processed using, or generated by the Polycaik Platform;
              (v) any improvements or modifications thereto; and (v) all
              Intellectual Property Rights embodied therein.
            </div>
          </Box>
          <Box className="stub">
            <span className="token token--slot">9.2.</span>
            <div>
              <span className="token header">
                Ownership of Participant Content.
              </span>
              As between you and Polycaik, you are the sole owner of Participant
              Content and you retain all rights in and to your Participant
              Content and the Intellectual Property Rights embodied therein,
              with the exception of (i) the license you grant to Polycaik under
              Section 8.3 (License to Participant Content); and (ii) where
              applicable, (a) the license that you (acting as a Contributor)
              grant to Developers under the Game Development Agreement; (b) the
              license that you (acting as a Developer) grant to Gamers under the
              Gamer Agreement; and (c) the license that you (acting as a Gamer),
              grant to Developers under the Gamer Agreement.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">10.</span>
            <div>
              <span className="token header">Availability.</span>
              Polycaik shall use commercially reasonable efforts to make the
              Polycaik Platform available to you without material interruption,
              subject to reasonable downtime for maintenance and error
              corrections.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">11.</span>
            <div>
              <span className="token header">Acceptable Use Policy.</span>
              You shall not use the Polycaik Platform (i) in a way prohibited by
              applicable law; (ii) to violate the legal rights of others; (iii)
              to attempt to gain or to gain unauthorized access to or disrupt
              any third party service, device, data, account or network; (iv) to
              distribute spam or malware; or (v) in a way that could materially
              harm the functionality or performance of the Polycaik Platform
              (the “<span className="token">AUP</span>”).
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">12.</span>
            <span className="token header">Fees and Compensation.</span>
          </Box>
          <Box className="stub">
            <span className="token">12.1.</span>
            <div>
              <span className="token header">Fees.</span>
              Polycaik may charge you a Fee, as specified in the your Access
              Plan, for access to and use of certain platform Services,
              features, or functionalities. The amount, billing frequency, and
              features associated with each subscription and fee may vary by
              Participant role and by the specific subscription plan or tier
              selected by you. Polycaik will invoice you on a monthly basis in
              arrear, and payment is due within thirty (30) days of your receipt
              of the applicable invoice. Past due amounts shall bear a late
              payment charge, until paid, at the rate of 1.5% per month or the
              maximum amount permitted by law, whichever is less. Polycaik
              reserves the right to modify the Pricing Schedule upon providing
              thirty (30) days’ prior notice to affected Participants. your
              continued use of the fee-based Services after such notice period
              constitutes your acceptance of the new Fees.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">12.2.</span>
            <div>
              <span className="token header">Revenue Streams and Splits.</span>
              Revenue generated through the Polycaik Platform may arise from
              multiple transaction types, including without limitation: (i)
              subscription fees paid by Gamers for access to basic Games, which
              are generally retained by Polycaik unless otherwise expressly
              provided in the Pricing Schedule or a specific written agreement;
              (ii) one-time “pay-to-play” purchases or licenses of premium Game
              by Gamers through the Polycaik Store; and (iii) purchases or
              licenses of Game Content or asset bundles by Developers from
              Contributors via the Content Library. When posting a Game to the
              Platform, Developer shall designate whether such Game is a basic
              or premium Game in accordance with the Access Plan and the
              Documentation. Developer may start designating a Game as a premium
              after posting at least two (2) basic Games to the Platform.
            </div>
          </Box>
          <Box className="stub" flexDirection="column">
            <Box className="stub">
              <span className="token">12.2.1.</span>
              <div>
                <span className="token header">Developer Revenue Share.</span>
                For each transaction where a Gamer purchases or licenses premium
                Game from a Developer through the Polycaik Platform, the gross
                revenue generated from that transaction will be shared between
                the Developer and Polycaik. Polycaik shall be entitled to a
                percentage of the gross revenue as its Platform Fee (the “
                <span className="token">Revenue Share</span>”), and the
                remaining portion of such gross revenue, after deduction of the
                Revenue Share and any applicable Taxes or permitted deductions,
                will be remitted to the applicable Developer (the “
                <span className="token">Developer Payout</span>”). Developer
                shall not be entitled to any Developer Payout or any other
                compensation with respect to basic Games.
              </div>
            </Box>
            <Box className="stub">
              <span className="token">12.2.2.</span>
              <div>
                <span className="token header">Contributor Revenue Share.</span>
                For each transaction where a Developer purchases or licenses
                Game Content or asset bundles that have been published for sale
                by a Contributor through the Content Library, the gross revenue
                generated from that transaction will be shared between the
                applicable Contributor and Polycaik, with Polycaik entitled to
                its Revenue Share and the remaining portion, after deduction of
                the Revenue Share and any applicable Taxes or permitted
                deductions, to be remitted to the applicable Contributor (the “
                <span className="token">Contributor Payout</span>”).
              </div>
            </Box>
            <Box className="stub">
              <span className="token">12.2.3.</span>
              <div>
                <span className="token header">Revenue Share.</span>
                The specific percentages, formulas, and other terms governing
                Polycaik’s Revenue Share, the corresponding Developer Payouts
                and Contributor Payouts, and any other relevant financial
                allocations for each type of Polycaik Platform mediated
                transaction (including, without limitation, premium Game Content
                purchases and asset or asset-bundle sales) will be detailed in
                Polycaik’s then-current Pricing Schedule. Polycaik may update
                such percentages, formulas, and policies from time to time in
                accordance with the notice provisions applicable to changes in
                the Pricing Schedule, and a your continued use of the applicable
                Services after the effective date of any such update constitutes
                your acceptance of the updated terms.
              </div>
            </Box>
          </Box>
          <Box className="stub">
            <span className="token">12.3.</span>
            <div>
              <span className="token header">
                Payment Terms; Past Due Amounts.
              </span>
              Your Access Plan specifies the Fees you owes under this Platform
              Agreement. Polycaik will invoice you on a monthly basis in arrear,
              and payment is due within thirty (30) days of your receipt of the
              applicable invoice. Past due amounts shall bear a late payment
              charge, until paid, at the rate of 1.5% per month or the maximum
              amount permitted by law, whichever is less.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">12.4.</span>
            <div>
              <span className="token header">Payouts.</span>
              Polycaik will remit all accrued amounts payable to Developers and
              Contributors arising from Polycaik Platform-mediated transactions,
              including without limitation Developer Payouts and Contributor
              Payouts (each, a “Payout”), on a periodic basis, within 45 days
              following the end of each calendar month, subject to any
              applicable minimum payout thresholds, chargebacks, refunds,
              reserves, compliance reviews, and withholding of Taxes or other
              permitted deductions as described in this Platform Agreement and
              the Pricing Schedule. Before any Payout is made, Polycaik will
              first deduct its applicable Platform Fees, including its Revenue
              Share and any Taxes or other amounts that Polycaik is permitted or
              required to withhold under this Platform Agreement or applicable
              law. Payouts will be made to the bank account, Payment Processor
              account (for example, PayPal), cryptocurrency wallet, or other
              payment method designated by the applicable Developer or
              Contributor in its Platform account. It is your responsibility to
              ensure that all payout-related information is accurate, complete,
              and current; Polycaik shall have no liability for delayed,
              misdirected, or failed Payouts resulting from incomplete or
              incorrect payout instructions provided by you.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">12.5.</span>
            <div>
              <span className="token header">Refunds and Cancellations.</span>
              Except as otherwise expressly provided in this Platform Agreement,
              the Pricing Schedule, or Polycaik’s then-current refund and
              cancellation policy, or as required by applicable law, (i) Fees
              that have already been charged for the then-current billing period
              are non-refundable, though you may cancel a subscription at any
              time to prevent future renewals, in which case you will generally
              retain access to the subscribed Services through the end of the
              paid billing period; and (ii) one-time payments for premium Game
              Content or Games, assets, or asset bundles are non-refundable once
              access to the relevant content has been granted. Polycaik may, in
              its discretion, issue refunds, credits, or other accommodations in
              particular cases without thereby obligating itself to provide
              similar relief in the future. Polycaik may publish, update, and
              from time to time revise a separate refund and cancellation policy
              on the Polycaik Platform, which is incorporated by reference into
              this Platform Agreement and governs the circumstances under which
              Participants may be eligible for refunds, credits, or other
              remedies in connection with subscriptions and one-time
              transactions.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">12.6.</span>
            <div>
              <span className="token header">Taxes.</span>
              All payments required by this Platform Agreement are exclusive of
              federal, state, local and foreign taxes, duties, tariffs, levies,
              withholdings and similar assessments (including without
              limitation, sales taxes, use taxes, withholding taxes, and value
              added taxes), and you agrees to bear and be responsible for the
              payment of all such taxes and charges, excluding taxes imposed on
              or measured by Polycaik’s net income. The Parties agree to comply
              with any formal requirements for purposes of obtaining the benefit
              of any double taxation convention or treaty that may exist from
              time to time between the Parties' jurisdictions. To the extent
              that withholding tax is required by law, you shall pay to Polycaik
              the Fees due under this Platform Agreement minus any withholding
              tax, and shall timely provide Polycaik with certificates or other
              evidence of such withholding.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">13.</span>
            <div>
              <span className="token header">Confidentiality.</span>
              Confidential Information under this Platform Agreement shall
              consist of all non-public information disclosed by one Party (the
              “<span className="token">Disclosing Party</span>”) to the other
              Party (the “<span className="token">Receiving Party</span>”),
              whether oral or in writing (including electronic transmission):
              (i) that is designated as “Confidential” or “Proprietary” by the
              Disclosing Party at the time of disclosure or within a reasonable
              period thereafter, or that by the nature of the circumstances
              surrounding disclosure, or the information itself, should in good
              faith be treated as confidential; and (ii) that concerns the
              technology, customers, finances, methods, research, processes, or
              procedures of the Disclosing Party (collectively, “
              <span className="token">Confidential Information</span>”).
            </div>
          </Box>
          <Box className="stub">
            <span className="token">13.1.</span>
            <div>
              <span className="token header">Non-Disclosure; Standard.</span>
              The Receiving Party shall retain the Disclosing Party’s
              Confidential Information in confidence and shall not use such
              Confidential Information except for purposes permitted under this
              Platform Agreement and the Privacy Policy. The Disclosing Party
              shall be entitled to disclose Confidential Information on a
              need-to-know basis to its employees, agents, and contractors,
              provided that the same are bound by non-disclosure and
              confidentiality obligations no less protective than those set out
              in this Platform Agreement. The Receiving Party shall use at least
              the same degree of care in safeguarding the Disclosing Party’s
              Confidential Information as it uses in safeguarding its own
              confidential information, but shall not use less than reasonable
              care and diligence.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">13.2.</span>
            <div>
              <span className="token header">Exceptions.</span>
              The Receiving Party’s obligations with respect to the Disclosing
              Party’s Confidential Information shall not apply to Confidential
              Information that the Receiving Party can demonstrate: (i) is or
              becomes a matter of public knowledge through no fault of the
              Receiving Party; (ii) was or becomes available to the Receiving
              Party on a non-confidential basis from a third party, provided
              that such third party is not bound by an obligation of
              confidentiality to the Disclosing Party with respect to such
              Confidential Information; or (iii) was independently developed by
              the Receiving Party without reference to the Disclosing Party’s
              Confidential Information.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">13.3.</span>
            <div>
              <span className="token header">Compelled Disclosure.</span>
              Nothing herein shall prevent a Receiving Party from disclosing
              Confidential Information as necessary pursuant to a court order or
              other lawful requirement of a governmental agency, or when
              disclosure is required by operation of law (including disclosures
              pursuant to applicable securities laws and regulations); provided,
              however, that prior to any such disclosure, the Receiving Party
              shall use reasonable efforts to (a) notify the Disclosing Party in
              writing of such requirement to disclose and (b) reasonably
              cooperate with the Disclosing Party, at the Disclosing Party’s
              expense, in protecting against or minimizing such disclosure, or
              in obtaining a protective order.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">13.4.</span>
            <div>
              <span className="token header">
                General Skills and Knowledge.
              </span>
              For the avoidance of doubt, it is agreed and understood that the
              provisions of this Section 13 (Confidentiality) do not apply to
              Residual Knowledge (if any) of Polycaik personnel, or to the use
              and improvement of other general skills and knowledge of such
              personnel. The term “
              <span className="token">Residual Knowledge</span>” means
              Confidential Information in non-tangible form that is retained in
              the unaided memory of persons who have had rightful access to such
              information.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">14.</span>
            <div>
              <span className="token header">
                Your Consent to Polycaik Privacy Practices.
              </span>
              The Privacy Policy explains, among other topics, how Polycaik
              treats Personal Information that you may provide to Polycaik
              through the Polycaik Platform. You hereby consent to the privacy
              practices as set out in Polycaik’s Privacy Policy.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">15.</span>
            <span className="token header">Third Party Materials.</span>
          </Box>
          <Box className="stub">
            <span className="token">15.1.</span>
            <div>
              <span className="token header">
                Third Party Copyleft Software.
              </span>
              The Polycaik Platform may contain Copyleft Open Source Software
              that Polycaik licenses from third parties (“
              <span className="token">Third Party Copyleft Software</span>”).
              Your rights and obligations with respect to Third Party Copyleft
              Software are governed by the copyleft open source software license
              applicable to such Third Party Copyleft Software, and are not
              governed by this Platform Agreement.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">15.2.</span>
            <div>
              <span className="token header">Third Party Websites.</span>
              The Polycaik Platform may contain links or references to other
              websites. Polycaik has no control over these other websites or
              their content and does not assume responsibility or liability for
              the content, opinions, or material available via such websites.
              Polycaik does not endorse the content of any third-party website,
              nor does Polycaik warrant that a third-party website will be free
              of computer viruses or other harmful code that can impact your
              computer or other web-access device. If you click on a link to any
              third-party website through the Polycaik Platform, please be aware
              that you are doing so at your own risk. Polycaik encourages you to
              review any third-party website’s terms of use and privacy policy
              as those, and not this Platform Agreement and Privacy Policy, are
              applicable to your use of their website and any information that
              such website collects.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">16.</span>
            <div>
              <span className="token header">Analytics.</span>
              Polycaik shall be entitled to collect, analyze, and otherwise use
              statistical data and other data related your use of the Polycaik
              Platform; provided such data does not constitute Personal
              Information (“<span className="token">Polycaik Analytics</span>”).
              No compensation shall be paid by Polycaik with respect to its use
              of Polycaik Analytics.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">17.</span>
            <span className="token header">
              Representations and Warranties.
            </span>
          </Box>
          <Box className="stub">
            <span className="token">17.1.</span>
            <div>
              <span className="token header">
                Mutual Representations and Warranties.
              </span>
              Each Party represents and warrants: (i) that its execution,
              delivery and performance of this Platform Agreement will not
              violate or constitute a default under any agreement of such Party;
              (iii) that it has the full right, power, and authority to enter
              into and be bound by the terms and conditions of this Platform
              Agreement and to perform its obligations under this Platform
              Agreement; (iv) that it will comply with applicable law; (v) that
              it will avoid deceptive, misleading, illegal or unethical
              practices that may be detrimental to the other Party; and, with
              respect to Polycaik and Participant (if Participant is a corporate
              entity) (vi) that it is duly organized, validly existing and in
              good standing, and is qualified and/or licensed to do business in
              all jurisdictions to the extent necessary to carry out its
              obligations under this Platform Agreement.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">17.2.</span>
            <div>
              <span className="token header">
                Participant Representations and Warranties.
              </span>
              You represent and warrant to Polycaik: (i) that you have secured
              all rights in Participant Content necessary to permit the access,
              use, and processing of Participant Content by Polycaik in
              accordance with this Platform Agreement; (ii) that your
              Participant Content will comply with applicable Requirements; and
              (iii) that you will not upload or release viruses or malicious
              code to the Polycaik Platform.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">17.3.</span>
            <div>
              <span className="token header">
                Polycaik Representations and Warranties.
              </span>
              Polycaik represents and warrants to you : (i) that the Services
              will be performed in accordance with accepted industry practice
              and by qualified individuals; (ii) that the Polycaik Platform will
              substantially conform to applicable Documentation, where such
              Documentation addresses the feature at issue, and to accepted
              industry practice where Documentation does not address the feature
              at issue; and (iii) that software components of the Polycaik
              Platform will be scanned using commercially available virus
              scanning software in accordance with accepted industry practice.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">17.4.</span>
            <div>
              <span className="token header">Disclaimers.</span>
              <span className="caution">
                EXCEPT AS EXPRESSLY PROVIDED IN SECTION 17.3 (POLYCAIK
                REPRESENTATIONS AND WARRANTIES), THE POLYCAIK PLATFORM AND THE
                SERVICES ARE DELIVERED ON AN “AS IS” AND “AS AVAILABLE” BASIS,
                AND POLYCAIK, ITS LICENSORS, AND ITS SERVICE PROVIDERS DISCLAIM:
                (i) ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING ANY IMPLIED
                WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE,
                AND NON-INFRINGEMENT; (ii) ANY RESPONSIBILITY OR LIABILITY FOR
                THE ACCURACY, COMPLETENESS, OR LEGALITY OF INFORMATION AVAILABLE
                THROUGH THE POLYCAIK PLATFORM; AND (iii) ANY RESPONSIBILITY OR
                LIABILITY FOR HARM RESULTING FROM DOWNLOADING OR ACCESSING
                CONTENT OR FILES THROUGH THE POLYCAIK PLATFORM, INCLUDING HARM
                CAUSED BY VIRUSES OR SIMILAR DESTRUCTIVE FEATURES INTRODUCED BY
                CONTRIBUTORS AND DEVELOPERS. EXCEPT AS EXPRESSLY PROVIDED IN
                SECTION 17.3 (POLYCAIK REPRESENTATIONS AND WARRANTIES), THE
                PARTICIPANT EXPRESSLY AGREES THAT ITS USE OF THE POLYCAIK
                PLATFORM IS AT THE PARTICIPANT’S SOLE RISK.
              </span>
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">18.</span>
            <div>
              <span className="token header">Limitation of Liability.</span>
              <span className="caution">
                UNDER NO CIRCUMSTANCES INCLUDING, BUT NOT LIMITED TO BREACH OF
                CONTRACT, TORT, OR NEGLIGENCE, SHALL POLYCAIK, ITS LICENSORS, OR
                ITS SERVICE PROVIDERS BE LIABLE FOR INDIRECT, SPECIAL,
                INCIDENTAL, PUNITIVE, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
                (INCLUDING LOST PROFITS) THAT ARISE OUT OF, OR ARE RELATED TO,
                THE PARTICIPANT’S USE OF THE POLYCAIK PLATFORM OR THE SERVICES.
                IN NO EVENT SHALL POLYCAIK’S TOTAL AGGREGATE LIABILITY TO THE
                PARTICIPANT FOR ANY LOSS, DAMAGE, OR CLAIM RELATED TO OR ARISING
                OUT OF THE POLYCAIK PLATFORM OR THE SERVICES EXCEED THE AMOUNT
                OF FEES THE PARTICIPANT HAS PAID TO POLYCAIK UNDER THIS
                AGREEMENT OVER THE ONE YEAR PERIOD IMMEDIATELY PROCEEDING THE
                CLAIM. THE PARTICIPANT ACKNOWLEDGES THAT POLYCAIK’S PRICING
                REFLECTS THE ALLOCATION OF RISKS, OWNERSHIP OF INTELLECTUAL
                PROPERTY RIGHTS, AND THE LIMITATION OF LIABILITY HEREIN.
              </span>
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">19.</span>
            <span className="token header">Indemnification.</span>
          </Box>
          <Box className="stub">
            <span className="token">19.1.</span>
            <div>
              <span className="token header">
                Indemnification by the Participant.
              </span>
              You agree to defend Polycaik from and against any demand, suit,
              action or other claim by a third party (including another
              Participant) that is related to or arises from (i) Participant
              Content submitted by you, including claims that this Participant
              Content misappropriates or infringes such third party’s
              Intellectual Property Rights, or (ii) your use of the Services in
              a manner not expressly permitted under this Platform Agreement or
              the applicable Access Plan (each a “
              <span className="token">Participant-Related Claim</span>”), and to
              indemnify Polycaik for settlement amounts or damages, liabilities,
              costs and expenses (including reasonable attorneys’ fees) awarded
              and arising out of any such Participant-Related Claim.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">19.2.</span>
            <div>
              <span className="token header">Indemnification by Polycaik.</span>
              Polycaik agrees to defend you against any demand, suit, action or
              other claim by a third party that the Polycaik Platform
              misappropriates or infringes the Intellectual Property Rights of
              such third party (each a “
              <span className="token">Polycaik-Related Claim</span>”), and to
              indemnify you for settlement amounts or damages, liabilities,
              costs and expenses (including reasonable attorneys’ fees) awarded
              and arising out of any such Polycaik-Related Claim.
            </div>
          </Box>
          <Box className="stub" flexDirection="column">
            <Box className="stub">
              <span className="token">19.2.1.</span>
              <div>
                <span className="token header">
                  Mitigation of Infringement.
                </span>
                Polycaik shall be entitled, at its option, to modify the
                Polycaik Platform to resolve third party infringement claims,
                provided such modifications do not materially degrade the
                features or performance of the Polycaik Platform. If Polycaik is
                unable at reasonable effort or expense to make such
                modifications, then Polycaik shall be entitled to terminate this
                Platform Agreement without further liability to you, and the
                effect of such termination shall be as set out in Section 24
                (Effect of Termination or Expiration).
              </div>
            </Box>
            <Box className="stub">
              <span className="token">19.2.2.</span>
              <div>
                <span className="token header">
                  No Responsibility for Participant-Related Claims.
                </span>
                For the avoidance of doubt, Polycaik is not responsible or
                liable for Participant-Related Claims, and has no
                indemnification obligations with respect to Participant-Related
                Claims.
              </div>
            </Box>
            <Box className="stub">
              <span className="token">19.2.3.</span>
              <div>
                <span className="token header">Available Remedy.</span>
                This Section 19.2 (Indemnification by Polycaik) states the
                entire liability of Polycaik, and your exclusive remedy, with
                respect to actual or alleged violation of third party
                Intellectual Property Rights by the Polycaik Platform or the
                Services.
              </div>
            </Box>
          </Box>
          <Box className="stub">
            <span className="token">19.3.</span>
            <div>
              <span className="token header">Conditions.</span>
              The obligations of the Party providing indemnity under this
              Section 19 (Indemnification) (the “
              <span className="token">Indemnifying Party</span>”)are conditioned
              on (i) the Party owed indemnity (the “
              <span className="token">Indemnified Party</span>”)providing prompt
              written notice of the claim and reasonable cooperation,
              information, and assistance in connection therewith (at the
              Indemnifying Party’s expense) and (ii) the Indemnifying Party
              having sole control and authority to defend, settle or compromise
              such claim. The Indemnified Party may participate in the defense
              at its sole cost and expense. The Indemnifying Party will not
              enter into any settlement that adversely affects the Indemnified
              Party’s rights or interest without its prior written approval, not
              to be unreasonably withheld. The Indemnifying Party shall not be
              responsible for any settlement it does not approve in writing.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">20.</span>
            <div>
              <span className="token header">
                Monitoring Polycaik Platform Use.
              </span>
              You agree that Polycaik has the right and discretion to monitor
              any activity and content associated with the Polycaik Platform.
              Polycaik may investigate any reported violation of this Platform
              Agreement or complaints relating to the Polycaik Platform or
              Participant Content, and may take any action that it believes is
              appropriate including, but not limited to, removing materials from
              the Polycaik Platform and terminating or suspending your access to
              the Polycaik Platform in accordance with Section 23 (Termination).
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">21.</span>
            <span className="token header">Changes to Platform Agreement.</span>
          </Box>
          <Box className="stub">
            <span className="token">21.1.</span>
            <div>
              <span className="token header">Right to Modify.</span>
              Polycaik reserves the right, in its discretion, to amend, modify,
              or replace terms of this Platform Agreement (including Access
              Plans, Documentation, and AUP) at any time to reflect changes in
              the law, to introduce new features, or to adjust its business
              practices. Polycaik will not make any change that materially
              degrades your Access Plan or materially impacts your rights and
              obligations under this Platform Agreement without providing you
              with the option to terminate as set forth in Section 21.4 (Your
              Right to Terminate for Changes to Agreement).
            </div>
          </Box>
          <Box className="stub">
            <span className="token">21.2.</span>
            <div>
              <span className="token header">Notice and Effective Date.</span>
              Polycaik will provide you with reasonable advance notice of any
              material changes to this Platform Agreement. Notice may be
              delivered by sending an email to the address associated with your
              account, by posting a notification within the Polycaik Platform,
              or by publishing the revised Agreement on the Polycaik Platform.
              The notice will specify the date on which the changes will become
              effective, which will be no less than thirty (30) days from the
              date of the notice, unless a shorter period is required by law
              (the “<span className="token">Change Effective Date</span>”).
            </div>
          </Box>
          <Box className="stub">
            <span className="token">21.3.</span>
            <div>
              <span className="token header">Acceptance of Changes.</span>
              Your continued access to or use of the Polycaik Platform after the
              specified effective date of any changes to this Platform Agreement
              will constitute your binding acceptance of the revised Agreement.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">21.4.</span>
            <div>
              <span className="token header">
                Your Right to Terminate for Changes to Agreement.
              </span>
              If you do not agree to the proposed changes, your sole and
              exclusive remedy is to terminate this Platform Agreement as
              described in Section 23.3 (Termination for Convenience by the
              Participant). Any continued use will be deemed acceptance as
              described in Section 21.3 (Acceptance of Changes).
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">22.</span>
            <div>
              <span className="token header">Term.</span>
              Your access to the Polycaik Platform shall commence upon the
              Effective Date and, unless earlier terminated pursuant to Section
              23 (Termination), continue for the period specified in your Access
              Plan (the “<span className="token">Term</span>”).
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">23.</span>
            <span className="token header">Termination.</span>
          </Box>
          <Box className="stub">
            <span className="token">23.1.</span>
            <div>
              <span className="token header">Termination for Cause.</span>
              In the event that either of the Parties is in breach of any
              material obligation under this Platform Agreement, the
              non-breaching Party may terminate this Platform Agreement for
              cause after first: (i) providing the breaching Party with written
              notice of the breach (a “
              <span className="token">Notice of Breach</span>”) and (ii)
              providing thereafter a thirty (30) day opportunity to cure
              beginning on the date of receipt by the alleged breaching Party of
              the Notice of Breach.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">23.2.</span>
            <div>
              <span className="token header">Suspension of Access.</span>
              Polycaik may suspend your access to the Polycaik Platform, to
              particular Games, or to specific Game Content, if Polycaik
              believes that you, your Participant Content, or your use of the
              Polycaik Platform violate this Platform Agreement or the AUP.
              Polycaik will timely notify you of such suspension, unless legal
              or regulatory reasons preventing Polycaik from doing so.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">23.3.</span>
            <div>
              <span className="token header">
                Termination for Convenience by Participant.
              </span>
              You may terminate your account with Polycaik for convenience by
              providing Polycaik with at least 30 days prior written notice, and
              such termination shall be effective as of the expiration of such
              30-day period.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">23.4.</span>
            <div>
              <span className="token header">Termination for Insolvency.</span>
              In the event a Party shall make an assignment for the benefit of
              creditors, or shall have a petition in bankruptcy filed for or
              against it that is not dismissed within sixty (60) days, the other
              Party shall have the right to terminate this Platform Agreement
              immediately upon providing written notice of such termination.
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">24.</span>
            <span className="token header">
              Effect of Termination or Expiration.
            </span>
          </Box>
          <Box className="stub">
            <span className="token">24.1.</span>
            <div>
              <span className="token header">Cessation of Access.</span>
              Upon termination or expiration of this Platform Agreement,
              Polycaik shall cease providing the Polycaik Platform to you, and
              all of your licenses under Section 8.1 (Participant Access Rights)
              shall end.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">24.2.</span>
            <div>
              <span className="token header">
                Return or Deletion of Applicable Confidential Information.
              </span>
              Each Party agrees that, upon termination or expiration of this
              Platform Agreement, and except to the extent it retains ongoing
              rights under Section 8 (Grant of Rights), it will either (i)
              return to the other Party all Confidential Information of such
              other Party and all derivatives thereof ; or (ii) destroy such
              Confidential Information (and all derivatives thereof) and certify
              in writing to such destruction. Notwithstanding the previous
              sentence, each Party may retain a copy of the other Party’s
              Confidential Information to the extent required for legal
              compliance or to the extent the same resides on backup servers;
              provided such retained Confidential Information (a) is later
              destroyed in the ordinary course of the Receiving Party’s business
              operations, and (b) remains subject to confidentiality, security,
              and other applicable requirements of this Platform Agreement.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">24.3.</span>
            <div>
              <span className="token header">
                No Effect on Prior Obligations.
              </span>
              Expiration or termination of this Platform Agreement shall not
              affect any obligation (including Fee obligations) which accrued
              prior to such expiration or termination.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">24.4.</span>
            <div>
              <span className="token header">Unpaid Fees.</span>
              In the event you (acting as a Contributor or Developer) owes
              Polycaik Fees as of expiration or termination of this Platform
              Agreement, Polycaik reserves the right to keep your Games and Game
              Content (as applicable) active and accessible on the Platform.
              During such time, Polycaik shall collect and retain one hundred
              percent (100%) of all revenue generated by such Games and Game
              Content to use as a set-off until all prior obligations are
              satisfied in full.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">24.5.</span>
            <div>
              <span className="token header">Removal of Games.</span>
              Subject to Section 24.4 (Unpaid Fees), upon expiration or
              termination of this Platform Agreement, Polycaik will remove your
              Games (where applicable) from the Platform, archive them for a
              period of ninety (90) days, and thereafter may permanently delete
              such Games and associated and data without further liability to
              the Developer.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">24.6.</span>
            <div>
              <span className="token header">
                Game Continuity and Continuing Rights.
              </span>
              If you, as of the termination or expiration of this Platform
              Agreement, have submitted Participant Content to the Platform (“
              <span className="token">Existing Content</span>”), and if a
              Developer has incorporated such Existing Content in a Game as of
              such termination or expiration (each an “
              <span className="token">Existing Game</span>”), then you agree
              that Polycaik’s license under Section 8.3 (License to Participant
              Content) shall remain in full force and effect after termination
              or expiration of this Platform Agreement and shall continue for
              the duration of the Developer’s rights in the Existing Game, as
              such rights are specified in the Game Development Agreement or the
              Gamer Agreement, as the case may be.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">24.7.</span>
            <div>
              <span className="token header">Survival.</span>
              Upon termination (for any reason) or expiration of this Platform
              Agreement, the following provisions shall survive and remain in
              effect: Section 8.5 (Feedback); Section 9 (Ownership); Section 13
              (Confidentiality); Section 16 (Analytics); Section 18 (Limitation
              of Liability); Section 19 (Indemnity); Section 24.6 (Game
              Continuity and Continuing Rights); Section 24.7 (Survival); and
              Section 25 (General).
            </div>
          </Box>
          <Box className="stub stub--title">
            <span className="token">25.</span>
            <span className="token header">General.</span>
          </Box>
          <Box className="stub">
            <span className="token">25.1.</span>
            <div>
              <span className="token header">
                Your Authority in a Representative Capacity.
              </span>
              If you are accessing the Platform in your capacity as an employee
              or other representative of an entity, you represent and warrant
              that you have the authority to contractually bind such entity to
              this Platform Agreement.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">25.2.</span>
            <div>
              <span className="token header">Governing Law.</span>
              All claims arising out of or relating to this Platform Agreement,
              the Polycaik Platform, or the Services shall be governed by the
              laws of the State of New Jersey, USA, without regard to its
              conflicts of law provisions.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">25.3.</span>
            <div>
              <span className="token header">Notices.</span>
              All notices under this Platform Agreement shall be in writing, in
              English and delivered to the Parties at their respective addresses
              set out below. Notices will be deemed to have been duly given (i)
              when received, if personally delivered; (ii) when receipt is
              electronically confirmed, if transmitted by facsimile or
              electronic mail; (iii) the day after being sent, if sent for next
              day delivery by recognized overnight delivery service; or (iv)
              upon receipt, if sent by certified or registered mail, return
              receipt requested.
              <Box
                className="stub"
                flexDirection="column"
                marginTop={2}
                gap={1}
              >
                <span>To Polycaik:</span>
                <Box className="stub" gap={1} flexDirection="column">
                  <span>Attention: Legal Counsel</span>
                  <span>
                    Mailing Address: 155 Willowbrook Boulevard, Suite 110 #6765,
                    Wayne, New Jersey 07470
                  </span>
                  <span>Email: legal@polycaik.com</span>
                </Box>
                <>
                  To Participant using the contact information Participant
                  provides during onboarding to the Polycaik Platform.
                </>
              </Box>
            </div>
          </Box>
          <Box className="stub">
            <span className="token"> 25.4.</span>
            <span className="token header">
              Dispute Resolution; Mandatory Arbitration.
            </span>
          </Box>
          <Box className="stub" flexDirection="column">
            <Box className="stub">
              <span className="token">25.4.1.</span>
              <div>
                <span className="token header">
                  Direction Discussions; Mediation.
                </span>
                If any controversy or claim arising out of or relating to this
                Platform Agreement, or the breach of any term hereof, cannot be
                settled through direct discussions, the Parties agree to
                endeavor first to settle the controversy or claim by
                confidential mediation conducted in Passaic County, New Jersey.
              </div>
            </Box>
            <Box className="stub">
              <span className="token">25.4.2.</span>
              <div>
                <span className="token header">Binding Arbitration.</span>
                If a controversy or claim is not otherwise resolved through
                direct discussions or mediation, it shall be resolved by
                confidential arbitration conducted in Passaic County, New
                Jersey, and administered by JAMS in accordance with its
                applicable rules. The JAMS rules for selection of an arbitrator
                shall be followed, except that the arbitrator shall be a former
                judge. Upon conclusion of any arbitration proceedings hereunder,
                the arbitrator shall render findings of fact and conclusions of
                law and a written opinion setting forth the basis and reasons
                for any decision the arbitrator has reached and shall deliver
                such documents to each Party to this Platform Agreement along
                with a signed copy of the award in accordance with applicable
                JAMS rules.
              </div>
            </Box>
            <Box className="stub">
              <span className="token">25.4.3.</span>
              <div>
                <span className="token header">
                  Enforcement; Jurisdiction; Arbitration Remedies.
                </span>
                Any judgment upon the award rendered by the arbitrator may be
                entered in any state or federal court that has requisite subject
                matter jurisdiction and that sits in the judicial district that
                includes Passaic County, New Jersey and the Parties agree to
                submit to the in personam jurisdiction of such courts for
                purposes of confirming any such award and entering judgment
                thereon. The arbitrator shall have the authority to grant any
                equitable and legal remedies that would be available in any
                judicial proceeding instituted to resolve a disputed matter,
                subject to any waivers of such remedies made by any Party in
                this Platform Agreement. The Parties hereto waive any and all
                objections that they may have as to jurisdiction or venue in any
                of the above courts.
              </div>
            </Box>
          </Box>
          <Box className="stub">
            <span className="token">25.5.</span>
            <div>
              <span className="token header">Independent Contractors.</span>
              Polycaik and you are independent contractors and this Platform
              Agreement will not establish any relationship of partnership,
              employment, franchise or agency for any purpose.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">25.6.</span>
            <div>
              <span className="token header">No Waiver; Entire Agreement.</span>
              Neither Party will be treated as having waived any rights by not
              exercising (or delaying the exercise of) any rights under this
              Platform Agreement. This Platform Agreement states all terms
              agreed between the Parties and cancels and replaces all other
              agreements between the Parties relating to its subject matter. If
              any term (or part of a term) of this Platform Agreement is
              invalid, illegal, or unenforceable, the rest of the Agreement will
              remain in effect.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">25.7.</span>
            <div>
              <span className="token header">
                No Third Party Beneficiaries.
              </span>
              The Parties hereby agree that there are no third party
              beneficiaries under this Platform Agreement.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">25.8.</span>
            <div>
              <span className="token header">Remedies.</span>
              Unless otherwise provided in this Platform Agreement, no remedy is
              exclusive, and a Party shall be entitled to assert all available
              rights in remedies.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">25.9.</span>
            <div>
              <span className="token header">Translations.</span>
              This Platform Agreement may be translated into languages other
              than English (collectively, “
              <span className="token">Translations</span>
              ”). Disputes shall be conducted in English, and the English
              version of this Platform Agreement shall control over the
              Translations.
            </div>
          </Box>
          <Box className="stub">
            <span className="token">25.10.</span>
            <div>
              <span className="token header">Force Majeure.</span>
              Neither Party shall be liable for a failure or delay in the
              performance of its obligations under this Platform Agreement due
              to strikes (except with respect to its own labor force),
              shortages, riots, pandemic, insurrection, acts of God, war,
              governmental action, power or connectivity interruptions, or other
              causes beyond the reasonable control of such Party.
            </div>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
